import React from "react";
import Swal from "sweetalert2";
import _ from "lodash";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import { Dropdown } from "Components/DropDown";
import { getRequest, patchRequest } from "Includes/api/apicall";
import { GET_URL } from "Includes/urls";
import { Alert } from "Includes/functions";
import { Actions, screenTypes } from "Constants/permissions";
import { getMobileApplicationSetting } from "Containers/GroupsPermissions/functions";

const getKeyValueMap = (list = [], keyField, valueField) =>
  (list || []).reduce((acc, item) => {
    if (item && item[keyField] !== undefined) {
      acc[item[keyField]] = item[valueField];
    }
    return acc;
  }, {});

const unwrapDetailPayload = (response) => {
  const body = response?.data;
  if (body == null) return {};
  if (body.data != null) return body.data;
  return body;
};

const extractPermissionCodes = (permissions = []) =>
  (Array.isArray(permissions) ? permissions : [])
    .map((permission) => {
      if (permission == null) return null;
      if (typeof permission === 'string') return permission;
      if (typeof permission === 'number') return null;
      return permission.codename ?? permission.code ?? null;
    })
    .filter(Boolean);

function PermissionsPageNew(props) {
  const [groupList, setGroupList] = React.useState([]);
  const [userList, setUserList] = React.useState([]);
  const [groupMap, setGroupMap] = React.useState({});
  const [userMap, setUserMap] = React.useState({});

  const [group, setGroup] = React.useState(0);
  const [user, setUser] = React.useState(0);
  const [menuType, setMenuType] = React.useState("web");

  const [permissionNames, setPermissionNames] = React.useState({});
  const [screens, setScreens] = React.useState([]);
  const [groupPermissionCodes, setGroupPermissionCodes] = React.useState([]);

  const [fetchingPermissions, setFetchingPermissions] = React.useState(false);
  const [submittingData, setSubmittingData] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState(false);
  const [alertData, setAlertData] = React.useState("");

  const showError = (message) => {
    setAlertData(message);
    setSnackbar(true);
  };

  const buildPermissionView = React.useCallback((assignedCodes = [], roleCodes = [], sourceActions = null) => {
    const baseActions = sourceActions ? _.cloneDeep(sourceActions) : _.cloneDeep(Actions);
    const activeScreens = new Set();

    Object.keys(baseActions).forEach((actionKey) => {
      const actionBlock = baseActions[actionKey];
      let hasPermission = false;

      screenTypes.forEach((type) => {
        if (actionBlock[type]?.permission_needed) {
          const actionCode = actionBlock[type].action_code;
          actionBlock[type].checked = assignedCodes.includes(actionCode);
          hasPermission = true;
        } else if (actionBlock[type]) {
          delete actionBlock[type];
        }
      });

      if (!hasPermission) {
        delete baseActions[actionKey];
        return;
      }

      if (baseActions[actionKey]?.type) {
        activeScreens.add(baseActions[actionKey].type);
      }
    });

    setPermissionNames(baseActions);
    setScreens(Array.from(activeScreens).sort());
    setGroupPermissionCodes(Array.from(new Set(roleCodes)));
    setFetchingPermissions(false);
  }, []);

  const loadGroupList = React.useCallback(() => {
    getRequest(GET_URL.groups.api, {}, props).then((response) => {
      if (response && response.status === 200) {
        const data = response?.data?.data || [];
        setGroupList(data);
        setGroupMap(getKeyValueMap(data, "id", "name"));
      }
    });
  }, [props]);

  const loadGroupUsers = React.useCallback((groupId) => {
    if (!groupId) {
      setUserList([]);
      setUserMap({});
      return;
    }

    getRequest(GET_URL.user.api, { groups: groupId }, props).then((response) => {
      if (response && response.status === 200) {
        const data = response?.data?.data || [];
        const formattedUsers = data.map((item) => {
          let name = item?.username || "";
          if (item?.staff) {
            name = `${item.staff.first_name || ""} ${item.staff.middle_name || ""} ${item.staff.last_name || ""}`.replace(/\s+/g, " ").trim();
          } else if (item?.student) {
            name = `${item.student.first_name || ""} ${item.student.middle_name || ""} ${item.student.last_name || ""}`.replace(/\s+/g, " ").trim();
          }
          return { id: item.id, name: name || item?.username || "" };
        });
        setUserList(formattedUsers);
        setUserMap(getKeyValueMap(data, "id", "username"));
      }
    });
  }, [props]);

  const loadGroupPermissions = React.useCallback((groupId, currentMenuType) => {
    if (!groupId) return;
    setFetchingPermissions(true);

    getRequest(`${GET_URL.groups.api}${groupId}/`, { menu_type: currentMenuType }, props).then((response) => {
      if (!(response && response.status === 200)) {
        buildPermissionView([], []);
        showError("Could not load saved permissions. Menus are shown so you can still assign them.");
        return;
      }

      const payload = unwrapDetailPayload(response);
      const groupNode = payload.group || payload;
      const permissions = groupNode.permissions || payload.permissions || [];
      const assignedCodes = extractPermissionCodes(permissions);

      if (currentMenuType === "app") {
        if (!GET_URL.permissionlist?.api) {
          setMenuType("web");
          showError("Mobile permission list is not configured. Showing Web permissions.");
          buildPermissionView(assignedCodes, []);
          return;
        }
        getRequest(GET_URL.permissionlist.api, { is_active: true, menu_type: "mobile" }, props).then((mobileResponse) => {
          if (mobileResponse && mobileResponse.status === 200) {
            const mobileActions = getMobileApplicationSetting(mobileResponse?.data?.data || []);
            buildPermissionView(assignedCodes, [], mobileActions);
          } else {
            buildPermissionView(assignedCodes, []);
          }
        });
      } else {
        buildPermissionView(assignedCodes, []);
      }
    });
  }, [buildPermissionView, props]);

  const loadUserPermissions = React.useCallback((userId, currentMenuType) => {
    if (!userId) return;
    setFetchingPermissions(true);

    getRequest(`${GET_URL.user.api}${userId}/`, { menu_type: currentMenuType }, props).then((response) => {
      if (!(response && response.status === 200)) {
        buildPermissionView([], []);
        showError("Could not load saved permissions. Menus are shown so you can still assign them.");
        return;
      }

      const payload = unwrapDetailPayload(response);
      const userData = payload.user || payload;
      const directPermissions = userData?.user_permissions || [];
      const groups = userData?.groups || [];
      const inheritedPermissions = groups.flatMap((groupItem) => groupItem?.permissions || []);

      const inheritedCodes = extractPermissionCodes(inheritedPermissions);
      const assignedCodes = Array.from(new Set([...extractPermissionCodes(directPermissions), ...inheritedCodes]));

      if (currentMenuType === "app") {
        if (!GET_URL.permissionlist?.api) {
          setMenuType("web");
          showError("Mobile permission list is not configured. Showing Web permissions.");
          buildPermissionView(assignedCodes, inheritedCodes);
          return;
        }
        getRequest(GET_URL.permissionlist.api, { is_active: true, menu_type: "mobile" }, props).then((mobileResponse) => {
          if (mobileResponse && mobileResponse.status === 200) {
            const mobileActions = getMobileApplicationSetting(mobileResponse?.data?.data || []);
            buildPermissionView(assignedCodes, inheritedCodes, mobileActions);
          } else {
            buildPermissionView(assignedCodes, inheritedCodes);
          }
        });
      } else {
        buildPermissionView(assignedCodes, inheritedCodes);
      }
    });
  }, [buildPermissionView, props]);

  React.useEffect(() => {
    loadGroupList();
  }, [loadGroupList]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const numericValue = Number(value);

    if (name === "group") {
      setGroup(numericValue);
      setUser(0);
      setPermissionNames({});
      setScreens([]);
      setGroupPermissionCodes([]);
      if (numericValue === 0) {
        setUserList([]);
        setUserMap({});
        setFetchingPermissions(false);
        return;
      }
      loadGroupUsers(numericValue);
      loadGroupPermissions(numericValue, menuType);
      return;
    }

    if (name === "user") {
      setUser(numericValue);
      if (numericValue === 0) {
        loadGroupPermissions(group, menuType);
      } else {
        loadUserPermissions(numericValue, menuType);
      }
    }
  };

  const isInheritedCode = (actionCode) => user !== 0 && groupPermissionCodes.includes(actionCode);

  const checkValues = (actionKey, name, checkAll) => {
    setPermissionNames((prev) => {
      const next = _.cloneDeep(prev);
      if (!next[actionKey]) return prev;

      const canToggle = (type) => {
        const permission = next[actionKey]?.[type];
        if (!permission) return false;
        if (isInheritedCode(permission.action_code)) return false;
        return true;
      };

      if (checkAll === true || checkAll === false) {
        screenTypes.forEach((type) => {
          if (!canToggle(type)) return;
          next[actionKey][type].checked = !checkAll;
        });
      } else if (canToggle(name)) {
        const nextValue = !next[actionKey][name].checked;

        if (name === "view" && !nextValue) {
          screenTypes.forEach((type) => {
            if (canToggle(type)) {
              next[actionKey][type].checked = false;
            }
          });
        } else if (name !== "view" && nextValue && canToggle("view")) {
          next[actionKey].view.checked = true;
        }

        next[actionKey][name].checked = nextValue;
      }

      return next;
    });
  };

  const getPermissionCodes = () => {
    const permissionCodes = [];

    Object.keys(permissionNames).forEach((actionKey) => {
      screenTypes.forEach((type) => {
        const permission = permissionNames[actionKey]?.[type];
        if (!permission?.checked) return;

        const actionCode = permission.action_code;
        if (user !== 0 && groupPermissionCodes.includes(actionCode)) return;
        permissionCodes.push(actionCode);
      });
    });

    return Array.from(new Set(permissionCodes));
  };

  const callPermissionService = (url, params) => {
    patchRequest(url, params, props).then((response) => {
      setSubmittingData(false);
      if (response && response.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Assigned permissions",
          showConfirmButton: false,
          timer: 1500,
        });
        if (user !== 0) {
          loadUserPermissions(user, menuType);
        } else if (group !== 0) {
          loadGroupPermissions(group, menuType);
        }
      }
    });
  };

  const submitPermissions = () => {
    if (!group) {
      showError("Please select a role.");
      return;
    }

    const permissionCodes = getPermissionCodes();
    const params = { menu_type: menuType };
    let url = `${GET_URL.groups.api}${group}/`;
    let removeLabel = `group: ${groupMap[group] || group}`;

    if (user !== 0) {
      params.user_permissions = permissionCodes;
      url = `${GET_URL.user.api}${user}/`;
      removeLabel = `user: ${userMap[user] || user}`;
    } else {
      params.permissions = permissionCodes;
    }

    setSubmittingData(true);
    if (permissionCodes.length === 0) {
      Swal.fire({
        title: "Are you sure?",
        text: `You want to remove all ${menuType} permissions for ${removeLabel}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, remove it!",
      }).then((result) => {
        if (result.value) {
          callPermissionService(url, params);
        } else {
          setSubmittingData(false);
        }
      });
      return;
    }

    callPermissionService(url, params);
  };

  const changeToggle = (_event, value) => {
    if (!value) return;
    setMenuType(value);
    if (user !== 0) {
      loadUserPermissions(user, value);
    } else if (group !== 0) {
      loadGroupPermissions(group, value);
    }
  };

  const renderPermissionTable = (screen) => {
    const rows = Object.keys(permissionNames).filter((key) => permissionNames[key]?.type === screen);
    if (rows.length === 0) return null;

    return (
      <Box key={screen} sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 700, color: "#1b4f9c", mb: 1.1, textTransform: "capitalize" }}>
          Screen: {screen.replace(/_/g, " ")}
        </Typography>
        <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f6f9ff" }}>
                <TableCell sx={{ fontWeight: 700, width: "40%" }}>Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>View</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Create</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Update</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((key) => {
                const row = permissionNames[key];
                const hasRestrictedRoles = ["view", "create", "update", "delete"].some(
                  (t) => Array.isArray(row[t]?.roles)
                );
                const allChecked = ["view", "create", "update", "delete"].some((t) => Boolean(row[t]?.checked));

                const renderCellCheckbox = (type) => {
                  if (!row[type]) return null;
                  const inherited = isInheritedCode(row[type].action_code);
                  const disabled = inherited || (Array.isArray(row[type].roles) && !row[type].roles.includes(group));
                  const title = inherited
                    ? "Granted by the role"
                    : (row[type].name || "");
                  return (
                    <Tooltip title={title} arrow>
                      <span>
                        <Checkbox
                          size="small"
                          checked={Boolean(row[type].checked)}
                          onChange={() => checkValues(key, type)}
                          disabled={disabled}
                        />
                      </span>
                    </Tooltip>
                  );
                };

                return (
                  <TableRow key={key} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {!hasRestrictedRoles && (
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            onChange={() => checkValues(key, "view", allChecked)}
                          />
                        )}
                        <Typography variant="body2">{row.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{renderCellCheckbox("view")}</TableCell>
                    <TableCell align="center">{renderCellCheckbox("create")}</TableCell>
                    <TableCell align="center">{renderCellCheckbox("update")}</TableCell>
                    <TableCell align="center">{renderCellCheckbox("delete")}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fff",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Permission Management</Typography>
        <Typography variant="body2" color="text.secondary">Select role and manage permissions</Typography>
      </Box>

      <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Dropdown data={groupList} name="group" value={group} onChange={onChange} label="Select Role" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Dropdown data={userList} name="user" value={user} onChange={onChange} label="Select User (Optional)" disabled={group === 0} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              <ToggleButtonGroup size="small" value={menuType} exclusive onChange={changeToggle}>
                <ToggleButton value="web">Web</ToggleButton>
                <ToggleButton value="app">App</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1, md: 1.5 }, minHeight: 240 }}>
        {group === 0 ? (
          <Box sx={{ py: 7, textAlign: "center" }}>
            <Typography color="text.secondary">Select a role to load permissions.</Typography>
          </Box>
        ) : fetchingPermissions ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>{screens.map((screen) => renderPermissionTable(screen))}</Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
        <Button variant="contained" onClick={submitPermissions} disabled={submittingData || group === 0}>
          Save Permissions
        </Button>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(false)}
      >
        <Alert onClose={() => setSnackbar(false)} severity="error">
          {alertData}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default PermissionsPageNew;
